import { useCallback, useState } from "react";

import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchDetail, UploadPayoutBatchInput } from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";

export interface UseUploadBatchResult {
  /** Resolves with the created batch, or null when the person cancelled the challenge or has to enrol. */
  upload: (input: UploadPayoutBatchInput) => Promise<PayoutBatchDetail | null>;
  isUploading: boolean;
  error: BatchError | null;
  clearError: () => void;
  twoFactor: UseTwoFactorActionResult<PayoutBatchDetail>;
}

/** `POST /` behind the step-up prompt: the file goes up with the code the person just typed. */
export function useUploadBatch(): UseUploadBatchResult {
  const twoFactor = useTwoFactorAction<PayoutBatchDetail>();
  const [error, setError] = useState<BatchError | null>(null);
  const { start } = twoFactor;

  const upload = useCallback(
    async (input: UploadPayoutBatchInput): Promise<PayoutBatchDetail | null> => {
      setError(null);
      try {
        return await start(async (code) => (await PayoutBatchesService.upload(input, code)).data);
      } catch (err: unknown) {
        setError(classifyBatchError(err));
        return null;
      }
    },
    [start],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    upload,
    isUploading: twoFactor.isRunning,
    error,
    clearError,
    twoFactor,
  };
}
